import { Injectable } from '@angular/core';
import { DatabaseService } from '../alcance/services/database.service';
import * as moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class AtaDBService extends DatabaseService {
    override classname: string = 'AtaDBService';
    override table = 'ata';
    override keyPath = 'uuid';
    override typeKey = 'TEXT';
    protected override useUuidKey = true;
    protected override migrationChunkSize = 50;
    private readonly maxLocalValueLength = 5 * 1024 * 1024;

    protected override migration: number = 3;

    override indices = [
        { name: "by_id", field: "id", unique: false, multiEntry: false },
        { name: "by_edificacao", field: "edificacao_id", unique: false, multiEntry: false },
        { name: "by_externo", field: "externo_id", unique: false, multiEntry: false },
        { name: "by_data", field: "dt_ata", unique: false, multiEntry: false },
        { name: "by_sinc", field: "sinc", unique: false, multiEntry: false }
    ];

    protected override collectObj(obj: any) {
        obj.dt_ata = (moment as any)(obj.dt_ata).format('YYYY-MM-DD');
        return super.collectObj(obj);
    }

    private async assertLocalValueReadable(key: number | string, index: string | null = null): Promise<void> {
        const field = index || this.keyPath;
        const result = await DatabaseService.DB.executeSql('SELECT length(value) as size FROM ' + this.table + ' WHERE ' + field + ' = ?', [key]);

        if(result.rows.length === 0){
            return;
        }

        const size = Number(result.rows.item(0).size || 0);

        if(size > this.maxLocalValueLength){
            const error: any = new Error('Este registro possui dados offline antigos muito grandes. Conecte-se à internet e abra novamente para atualizar os dados.');
            error.code = 'LOCAL_RECORD_TOO_LARGE';
            throw error;
        }
    }

    private async assertLocalListReadable(): Promise<void> {
        const result = await DatabaseService.DB.executeSql('SELECT count(*) as total FROM ' + this.table + ' WHERE length(value) > ?', [this.maxLocalValueLength]);

        if(Number(result.rows.item(0).total || 0) > 0){
            const error: any = new Error('Existem registros offline antigos muito grandes. Conecte-se à internet para atualizar os dados antes de abrir esta lista.');
            error.code = 'LOCAL_RECORD_TOO_LARGE';
            throw error;
        }
    }

    private isLocalValueTooLarge(object: any): boolean {
        try{
            return JSON.stringify(object).length > this.maxLocalValueLength;
        }catch(_){
            return true;
        }
    }

    private compactForLocalCache(object: any): any {
        const compact = {...object};
        delete compact.descricao;
        delete compact.documentos;
        return compact;
    }

    override async sinc(object: any): Promise<any> {
        return super.sinc(this.isLocalValueTooLarge(object) ? this.compactForLocalCache(object) : object);
    }

    override async getByKey(key: number | string, index: string | null = null): Promise<any> {
        if(!this.initialized){
            await this.createTable();
            return this.getByKey(key, index);
        }

        await this.assertLocalValueReadable(key, index);
        return super.getByKey(key, index);
    }

    override async paginate(filter: any = {}): Promise<any> {
        await this.assertLocalListReadable();
        return super.paginate(filter);
    }

    override async getAll(params: any = null, headers: any = null): Promise<any> {
        await this.assertLocalListReadable();
        return super.getAll(params, headers);
    }

    override async sincArray(array: any[]): Promise<any> {
        if(!array || array.length === 0){
            return super.sincArray(array);
        }

        const data: any[] = [];

        for(const item of array){
            const next = {...item};

            if(!Object.prototype.hasOwnProperty.call(next, 'descricao') && next[this.keyPath]){
                try{
                    const cached = await this.getByKey(next[this.keyPath]);

                    if(cached?.descricao){
                        next.descricao = cached.descricao;
                    }

                    if(!Object.prototype.hasOwnProperty.call(next, 'documentos') && cached?.documentos){
                        next.documentos = cached.documentos;
                    }
                }catch(_){
                    // Ata ainda nao foi aberta/cacheada com conteudo completo.
                }
            }

            data.push(this.isLocalValueTooLarge(next) ? this.compactForLocalCache(next) : next);
        }

        return super.sincArray(data);
    }

    protected override filterSql(sql: string, params: any): string {
        if(params?.dt_ata){
            if(sql.indexOf('WHERE') == -1)
                sql += ' WHERE';
            else
                sql += ' AND';

            sql += " by_data = '" + (moment as any)(params.dt_ata).format('YYYY-MM-DD') + "'";
        }

        if(params?.externo_id){
            if(sql.indexOf('WHERE') == -1)
                sql += ' WHERE';
            else
                sql += ' AND';

            sql += " by_externo = '" + params.externo_id + "'";
        }

        return sql;
    }
}
